/**
 * LinkHostDashboardWizard
 * 8.2 Phase B - admin slide-down inside UserDetailPanel that creates everything
 * needed to give a host a working /host/v2/{event_id} dashboard with one click.
 *
 * Steps:
 *   1. Pick or create org
 *   2. Pick category
 *   3. Tick what to auto-generate, click Generate
 */
import { useEffect, useState } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ChevronDown, ChevronUp, LayoutDashboard, Building2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

export default function LinkHostDashboardWizard({ user }) {
  const [open, setOpen] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [createNewOrg, setCreateNewOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generated, setGenerated] = useState(null);
  const [auto, setAuto] = useState({
    provider_profile: true,
    event_record: true,
    host_access_assignment: true,
    seed_services: true,
    send_magic_link: false,
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const token = localStorage.getItem("session_token");
        // Categories
        const cr = await fetch(`${API}/categories/branding`);
        if (cr.ok) {
          const data = await cr.json();
          setCategories(Object.values(data));
          if (user.primary_category_id) setSelectedCategoryId(user.primary_category_id);
        }
        // Orgs (admin endpoint)
        const or = await fetch(`${API}/admin/database/organizations?limit=200`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (or.ok) {
          const data = await or.json();
          const list = (data.documents || data.rows || data || []).filter((x) => x.organization_id);
          setOrgs(list);
          if (user.organization_id_primary) setSelectedOrgId(user.organization_id_primary);
        }
      } catch (_) {
        /* ignore */
      }
    })();
  }, [open, user.user_id, user.organization_id_primary, user.primary_category_id]);

  const handleGenerate = async () => {
    if (!createNewOrg && !selectedOrgId) return toast.error("Pick or create an org");
    if (createNewOrg && !newOrgName.trim()) return toast.error("Enter a new org name");
    if (!selectedCategoryId) return toast.error("Pick a category");
    setGenerating(true);
    try {
      const token = localStorage.getItem("session_token");
      const body = {
        user_id: user.user_id,
        organization_id: createNewOrg ? null : selectedOrgId,
        new_org_name: createNewOrg ? newOrgName.trim() : null,
        category_id: selectedCategoryId,
        auto: auto,
      };
      const res = await fetch(`${API}/admin/host-dashboard/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setGenerated(data);
        toast.success("Host dashboard linked.");
      } else {
        const err = await res.text();
        toast.error("Failed to link: " + err.slice(0, 100));
      }
    } catch (e) {
      toast.error("Network error linking dashboard");
    } finally {
      setGenerating(false);
    }
  };

  const cat = categories.find((c) => c.category_id === selectedCategoryId);

  return (
    <div className="mt-3 border border-gray-200 rounded-lg bg-gray-50" data-testid="link-host-dashboard-wizard">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-3 hover:bg-gray-100 rounded-lg transition-colors"
        data-testid="link-host-dashboard-toggle"
      >
        <div className="flex items-center gap-2">
          <LayoutDashboard className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-900">Host Dashboard Link</span>
          {generated && (
            <Badge className="bg-green-100 text-green-700 text-[10px]">
              <Check className="w-3 h-3 mr-1" />
              Linked
            </Badge>
          )}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>

      {open && (
        <div className="p-3 pt-0 space-y-4">
          {/* Step 1 - Org */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">1. Organization</label>
            <div className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="org-mode"
                  checked={!createNewOrg}
                  onChange={() => setCreateNewOrg(false)}
                  data-testid="org-mode-existing"
                />
                <span>Use existing</span>
              </label>
              {!createNewOrg && (
                <select
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  className="w-full h-9 border border-gray-200 rounded-md px-2 text-sm bg-white"
                  data-testid="org-select"
                >
                  <option value="">Select an org</option>
                  {orgs.map((o) => (
                    <option key={o.organization_id} value={o.organization_id}>
                      {o.display_name || o.name || o.organization_id}
                    </option>
                  ))}
                </select>
              )}
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="org-mode"
                  checked={createNewOrg}
                  onChange={() => setCreateNewOrg(true)}
                  data-testid="org-mode-new"
                />
                <span>Create new</span>
              </label>
              {createNewOrg && (
                <input
                  type="text"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  placeholder="e.g. Privé Beauté"
                  className="w-full h-9 border border-gray-200 rounded-md px-2 text-sm"
                  data-testid="org-new-name"
                />
              )}
            </div>
          </div>

          {/* Step 2 - Category */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">2. Category</label>
            <select
              value={selectedCategoryId}
              onChange={(e) => setSelectedCategoryId(e.target.value)}
              className="w-full h-9 border border-gray-200 rounded-md px-2 text-sm bg-white"
              data-testid="category-select"
            >
              <option value="">Select a category</option>
              {categories.map((c) => (
                <option key={c.category_id} value={c.category_id}>
                  {c.display_name || c.category_id}
                </option>
              ))}
            </select>
            {cat && (
              <div className="mt-1.5 flex items-center gap-2 text-xs">
                <span
                  className="inline-block w-3 h-3 rounded-full border border-gray-200"
                  style={{ backgroundColor: cat.dark_hex }}
                />
                <span className="text-gray-600">Brand color {cat.dark_hex}</span>
              </div>
            )}
          </div>

          {/* Step 3 - Auto-generate */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">3. Auto-generate</label>
            <div className="space-y-1.5">
              {[
                ["provider_profile", "Provider profile"],
                ["event_record", "Event record (host_dashboard)"],
                ["host_access_assignment", "Host access assignment (owner)"],
                ["seed_services", "Pre-seed services from category template"],
                ["send_magic_link", "Send activation magic link"],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={auto[key]}
                    onCheckedChange={(v) => setAuto({ ...auto, [key]: !!v })}
                    data-testid={`auto-${key}`}
                  />
                  <span className="text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Action */}
          <div className="flex gap-2 pt-1">
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="flex-1 h-9 bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="generate-host-dashboard-btn"
            >
              {generating ? "Generating..." : "Generate dashboard"}
            </Button>
            {generated?.event_id && (
              <Button
                variant="outline"
                onClick={() => window.open(`/host/v2/${generated.event_id}`, "_blank")}
                className="h-9"
                data-testid="open-host-dashboard-btn"
              >
                Open dashboard
              </Button>
            )}
          </div>

          {generated && (
            <div className="p-2 bg-green-50 border border-green-200 rounded-md text-xs text-green-800 space-y-1">
              <div className="flex items-center gap-1.5"><Building2 className="w-3 h-3" /> org_id: {generated.organization_id}</div>
              <div className="flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> event_id: {generated.event_id}</div>
              {generated.provider_id && <div className="flex items-center gap-1.5"><Check className="w-3 h-3" /> provider_id: {generated.provider_id}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
